/**
 * The half that touches the browser.
 *
 * `audio.js` decides what plays and when; this makes the noise. Kept thin for
 * the same reason `input.js` is thin next to `inputMap.js` and `saveStore.js`
 * is thin next to `save.js`: everything awkward in here is about the Web Audio
 * API rather than about the game, and none of it should be able to break a
 * rule.
 *
 * **Nothing is shipped as a file.** Every sound is synthesised from the recipe
 * in `audio.json` — six voices, each a few oscillators and an envelope — so the
 * game has audio with no assets at all and nothing to download. They are
 * placeholders in exactly the sense the mannequin is: the shape is right and
 * the material is not.
 *
 * **Real recordings drop in without touching this file.** A sound that grows a
 * `src` is fetched, decoded, memoised and played as a buffer; a sound without
 * one is synthesised. That branch is the whole migration path, and it is a data
 * change on one line of JSON.
 *
 * Three things about browsers that are not about the game:
 *
 *   - An `AudioContext` starts **suspended** until a user gesture, and a game
 *     that made no sound until somebody found the right click would read as
 *     broken. It resumes itself on the first pointer or key event, once.
 *   - There may be no `AudioContext` at all — an old browser, a locked-down
 *     one, or `node`. Every entry point returns quietly rather than throwing,
 *     and `audio.js` treats a missing device as a silent game.
 *   - Voices are capped. A hitstop, a train and a crowd grab can all land on
 *     one frame, and an uncapped synth answers that with a click.
 */

/** A shared noise buffer, built once. Two seconds is longer than any decay. */
let noiseBuffer = null;

function noiseFor(ctx) {
  if (noiseBuffer && noiseBuffer.sampleRate === ctx.sampleRate) return noiseBuffer;
  const frames = Math.floor(ctx.sampleRate * 2);
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i += 1) data[i] = Math.random() * 2 - 1;
  noiseBuffer = buffer;
  return buffer;
}

/** The two ends of a frequency, whether the file gave one number or two. */
const ends = (hz) => (Array.isArray(hz) ? [hz[0], hz[hz.length - 1]] : [hz, hz]);

/**
 * An exponential ramp cannot pass through zero, which is what a naive gain
 * envelope does at the end. Everything decays to this instead — far enough
 * below audible that it is silence, and positive, so the ramp is legal.
 */
const FLOOR = 0.0001;

export function createWebAudioDevice({ limit = 10, onWarn = () => {} } = {}) {
  const Ctx = globalThis.AudioContext ?? globalThis.webkitAudioContext;
  if (!Ctx) {
    onWarn('no AudioContext; the game will be silent');
    return null;
  }

  let ctx;
  try {
    ctx = new Ctx();
  } catch (error) {
    onWarn(`could not open an audio context: ${error?.message ?? error}`);
    return null;
  }

  const master = ctx.createGain();
  master.gain.value = 1;
  master.connect(ctx.destination);

  // --- the crowd, which is a level rather than a sound ----------------------
  //
  // Built once and left running for the life of the page. A bed that was
  // started and stopped would click every time the room went quiet, and the
  // only thing that ever changes about it is how loud it is.
  const bedGain = ctx.createGain();
  bedGain.gain.value = 0;
  const bedFilter = ctx.createBiquadFilter();
  bedFilter.type = 'bandpass';
  bedFilter.Q.value = 0.7;
  bedGain.connect(master);
  bedFilter.connect(bedGain);
  const bedSource = ctx.createBufferSource();
  bedSource.buffer = noiseFor(ctx);
  bedSource.loop = true;
  bedSource.connect(bedFilter);
  bedSource.start();

  /** How many one-shots are still ringing. */
  let voices = 0;
  /** Decoded files, by url. A second fighter does not pay for the same sound. */
  const buffers = new Map();

  // An audio context is suspended until somebody interacts with the page, and
  // a game that waited to be asked twice would read as broken. Once, and then
  // the listeners take themselves off.
  let resumed = ctx.state === 'running';
  function wake() {
    if (resumed) return;
    resumed = true;
    ctx.resume?.().catch(() => {});
    for (const name of ['pointerdown', 'keydown', 'touchstart']) {
      globalThis.removeEventListener?.(name, wake);
    }
  }
  if (!resumed) {
    for (const name of ['pointerdown', 'keydown', 'touchstart']) {
      globalThis.addEventListener?.(name, wake, { passive: true });
    }
  }

  /** A gain node that fades from `peak` to nothing over `decay`, then tidies up. */
  function envelope(peak, decay, { attack = 0.004 } = {}) {
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(FLOOR, now);
    gain.gain.exponentialRampToValueAtTime(Math.max(FLOOR, peak), now + attack);
    gain.gain.exponentialRampToValueAtTime(FLOOR, now + attack + decay);
    gain.connect(master);
    voices += 1;
    // Disconnected rather than left to the collector: a few hundred orphaned
    // nodes an hour is a leak nobody would think to look for.
    setTimeout(
      () => {
        try {
          gain.disconnect();
        } catch {
          // Already gone, which is fine.
        }
        voices = Math.max(0, voices - 1);
      },
      (attack + decay) * 1000 + 60
    );
    return gain;
  }

  /** One oscillator, started now and stopped when the envelope is done. */
  function osc({ shape = 'sine', from, to, decay, out, rate = 1 }) {
    const now = ctx.currentTime;
    const node = ctx.createOscillator();
    node.type = shape;
    node.frequency.setValueAtTime(Math.max(1, from * rate), now);
    if (to !== from) {
      node.frequency.exponentialRampToValueAtTime(Math.max(1, to * rate), now + decay);
    }
    node.connect(out);
    node.start(now);
    node.stop(now + decay + 0.02);
    return node;
  }

  /** A burst of the shared noise, band-limited, optionally sweeping. */
  function hiss({ from, to, decay, out, rate = 1, q = 1 }) {
    const now = ctx.currentTime;
    const source = ctx.createBufferSource();
    source.buffer = noiseFor(ctx);
    // A different slice each time, so two bursts are not the same burst.
    const offset = Math.random() * (source.buffer.duration - decay - 0.05);
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = q;
    filter.frequency.setValueAtTime(Math.max(20, from * rate), now);
    if (to !== from) {
      filter.frequency.exponentialRampToValueAtTime(Math.max(20, to * rate), now + decay);
    }
    source.connect(filter);
    filter.connect(out);
    source.start(now, Math.max(0, offset), decay + 0.05);
    return source;
  }

  /**
   * The six voices, each a recipe rather than a special case.
   *
   * Adding a seventh is a function here and a name in `VOICES`; it is not a
   * branch anywhere in `audio.js`, which never learns what a sound is made of.
   */
  const VOICE = {
    /** A body being hit: a pitch dropping fast, with grit over it. */
    thud(sound, out, rate) {
      const [hz] = ends(sound.hz);
      osc({ shape: 'sine', from: hz, to: hz * (sound.drop ?? 0.5), decay: sound.decay, out, rate });
      if (sound.noise) {
        const grit = ctx.createGain();
        grit.gain.value = sound.noise;
        grit.connect(out);
        hiss({ from: hz * 4, to: hz, decay: sound.decay * 0.6, out: grit, rate, q: 0.8 });
      }
    },
    /** A guard taking one: short, high, nothing behind it. */
    click(sound, out, rate) {
      const [hz] = ends(sound.hz);
      hiss({ from: hz, to: hz * 0.6, decay: sound.decay, out, rate, q: 3 });
    },
    /** Metal: two detuned partials that do not agree, ringing on. */
    clang(sound, out, rate) {
      const [hz] = ends(sound.hz);
      for (const ratio of [1, 1.47, 2.09]) {
        osc({ shape: 'triangle', from: hz * ratio, to: hz * ratio * 0.96, decay: sound.decay, out, rate });
      }
      if (sound.noise) hiss({ from: hz * 3, to: hz, decay: sound.decay * 0.4, out, rate, q: 2 });
    },
    /** Air, or a room, or a fighter getting up. */
    noise(sound, out, rate) {
      const [from, to] = ends(sound.hz);
      hiss({ from, to, decay: sound.decay, out, rate, q: 0.9 });
    },
    /** A bell, or a sting: held partials with a long tail. */
    tone(sound, out, rate) {
      const [hz] = ends(sound.hz);
      const partials = sound.partials ?? [1];
      for (const [index, ratio] of partials.entries()) {
        const voice = ctx.createGain();
        // Upper partials quieter, or the thing reads as a chord.
        voice.gain.value = 1 / (index + 1);
        voice.connect(out);
        osc({
          shape: sound.shape ?? 'sine',
          from: hz * ratio,
          to: hz * ratio,
          decay: sound.decay,
          out: voice,
          rate,
        });
      }
    },
    /** Something arriving or leaving: a pitch travelling a long way. */
    sweep(sound, out, rate) {
      const [from, to] = ends(sound.hz);
      osc({ shape: sound.shape ?? 'sawtooth', from, to, decay: sound.decay, out, rate });
      if (sound.noise) {
        const air = ctx.createGain();
        air.gain.value = sound.noise;
        air.connect(out);
        hiss({ from: from * 8, to: to * 8, decay: sound.decay, out: air, rate, q: 0.5 });
      }
    },
  };

  /** A recorded sound, when one has been dropped in. Fetched once, then held. */
  async function sample(url, cue) {
    if (!buffers.has(url)) {
      buffers.set(
        url,
        fetch(url)
          .then((response) => response.arrayBuffer())
          .then((bytes) => ctx.decodeAudioData(bytes))
          .catch((error) => {
            onWarn(`could not load ${url}: ${error?.message ?? error}`);
            return null;
          })
      );
    }
    const buffer = await buffers.get(url);
    if (!buffer) return;
    const gain = ctx.createGain();
    gain.gain.value = cue.gain;
    gain.connect(master);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = cue.rate;
    source.connect(gain);
    source.start();
    source.onended = () => {
      try {
        gain.disconnect();
      } catch {
        // Already gone.
      }
    };
  }

  return {
    get context() {
      return ctx;
    },
    get voices() {
      return voices;
    },
    get running() {
      return ctx.state === 'running';
    },

    /**
     * One cue, as `audio.js` decided it: a sound, a gain and a playback rate.
     *
     * The rate is applied to *frequency* for a synthesised voice and to
     * `playbackRate` for a recorded one, which is the same thing heard two
     * ways — and it is why the pitch roll lives in the rules rather than here.
     */
    play(cue) {
      if (!cue) return false;
      if (voices >= limit) return false;
      wake();
      const { sound } = cue;
      if (sound.src) {
        sample(sound.src, cue);
        return true;
      }
      const build = VOICE[sound.voice];
      if (!build) return false;
      const out = envelope(cue.gain, sound.decay);
      build(sound, out, cue.rate);
      return true;
    },

    /** How loud the room is. Ramped, because a jump in a running bed clicks. */
    bed(level, config = {}) {
      const now = ctx.currentTime;
      const [from, to] = ends(config.hz ?? [200, 1000]);
      bedGain.gain.setTargetAtTime(Math.max(0, level), now, 0.08);
      // The room does not only get louder, it gets brighter — a full platform
      // is more voices in the top end rather than the same voices turned up.
      const want = from + (to - from) * Math.min(1, Math.max(0, level / (config.gain || 1)));
      bedFilter.frequency.setTargetAtTime(Math.max(20, want), now, 0.2);
      return level;
    },

    /** Everything ringing, cut. Used by the mute so it is immediate. */
    silence() {
      const now = ctx.currentTime;
      master.gain.cancelScheduledValues(now);
      master.gain.setValueAtTime(0, now);
      master.gain.setTargetAtTime(1, now + 0.05, 0.02);
      return true;
    },

    dispose() {
      try {
        bedSource.stop();
      } catch {
        // Already stopped.
      }
      ctx.close?.();
    },
  };
}
